import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Typography,
  Tag,
  Spin
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { minimumVersionMarkdownsApi } from '../api/minimumVersionMarkdowns';
import { appsApi } from '../api/apps';
import { MinimumVersionMarkdown, CreateMinimumVersionMarkdownRequest, UpdateMinimumVersionMarkdownRequest, App } from '../types';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface MinimumVersionsProps {
  selectedAppId?: number;
}

const MinimumVersions: React.FC<MinimumVersionsProps> = ({ selectedAppId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMarkdown, setEditingMarkdown] = useState<MinimumVersionMarkdown | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: markdownsResponse, isLoading } = useQuery({
    queryKey: ['minimum-version-markdowns', selectedAppId],
    queryFn: () => minimumVersionMarkdownsApi.getMinimumVersionMarkdowns(1, 1000, selectedAppId),
    refetchOnMount: true,
    staleTime: 0, // Data luôn được coi là stale, sẽ refetch mỗi lần
  });

  const { data: appsResponse } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.getApps(),
    enabled: isModalVisible, // Chỉ fetch khi modal mở
  });

  const createMutation = useMutation({
    mutationFn: minimumVersionMarkdownsApi.createMinimumVersionMarkdown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minimum-version-markdowns'] });
      message.success('Tạo minimum version markdown thành công!');
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Create minimum version markdown error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi tạo minimum version markdown: ' + errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ appId, data }: { appId: number; data: UpdateMinimumVersionMarkdownRequest }) => 
      minimumVersionMarkdownsApi.updateMinimumVersionMarkdown(appId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minimum-version-markdowns'] });
      message.success('Cập nhật minimum version markdown thành công!');
      setIsModalVisible(false);
      setEditingMarkdown(null);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Update minimum version markdown error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi cập nhật minimum version markdown: ' + errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: minimumVersionMarkdownsApi.deleteMinimumVersionMarkdown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minimum-version-markdowns'] });
      message.success('Xóa minimum version markdown thành công!');
    },
    onError: (error: any) => {
      console.error('Delete minimum version markdown error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi xóa minimum version markdown: ' + errorMessage);
    },
  });

  const handleCreate = () => {
    setEditingMarkdown(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (markdown: MinimumVersionMarkdown) => {
    setEditingMarkdown(markdown);
    form.setFieldsValue({
      minimum_version: markdown.minimum_version,
      markdown_content: markdown.markdown_content,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (appId: number) => {
    deleteMutation.mutate(appId);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingMarkdown) {
        updateMutation.mutate({
          appId: editingMarkdown.app_id,
          data: {
            minimum_version: values.minimum_version,
            markdown_content: values.markdown_content,
          }
        });
      } else {
        createMutation.mutate(values as CreateMinimumVersionMarkdownRequest);
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingMarkdown(null);
    form.resetFields();
  };

  const getAppName = (appId: number) => {
    return appsResponse?.find((app: App) => app.id === appId)?.name || appId.toString();
  };

  // Data is already filtered by backend
  const markdowns = markdownsResponse?.data || [];

  const columns = [
    {
      title: 'App',
      dataIndex: 'app_id',
      key: 'app_id',
      render: (appId: number) => getAppName(appId),
    },
    {
      title: 'Minimum Version',
      dataIndex: 'minimum_version',
      key: 'minimum_version',
    },
    {
      title: 'Nội dung',
      dataIndex: 'markdown_content',
      key: 'markdown_content',
      render: (content?: string) => (
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          maxWidth: '300px'
        }}>
          {content || '-'}
        </div>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: MinimumVersionMarkdown) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc muốn xóa minimum version markdown này?"
            onConfirm={() => handleDelete(record.app_id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <span style={{ color: '#666', fontSize: '16px' }}>Đang tải dữ liệu minimum versions...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Minimum Version Markdowns</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Thêm Minimum Version
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={markdowns}
        loading={isLoading}
        rowKey="app_id"
        pagination={{
          total: markdownsResponse?.total || 0,
          pageSize: markdownsResponse?.page_size || 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} minimum version markdowns`,
        }}
      />

      <Modal
        title={editingMarkdown ? "Sửa Minimum Version Markdown" : "Thêm Minimum Version Markdown mới"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {!editingMarkdown && (
            <Form.Item
              name="app_id"
              label="App"
              rules={[{ required: true, message: 'Vui lòng chọn app!' }]}
            >
              <Select placeholder="Chọn app">
                {appsResponse?.map((app: App) => (
                  <Option key={app.id} value={app.id}>
                    {app.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="minimum_version"
            label="Minimum Version"
            rules={[{ required: true, message: 'Vui lòng nhập minimum version!' }]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          <Form.Item
            name="markdown_content"
            label="Nội dung Markdown"
          >
            <TextArea
              placeholder="Nhập nội dung markdown cho minimum version..."
              rows={10}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MinimumVersions;
